import React, { memo, useMemo, useState, useCallback, useRef } from 'react';
import { getGroupBoundingBox, resizeGroup, getGroupComponentIds } from '../utils/groupUtils';
import { calculateRowHeight } from '../utils/dashboardStyleUtils';
import './GroupBoundingBox.css';

/**
 * 그룹 바운딩 박스 컴포넌트
 * 그룹에 속한 모든 컴포넌트를 감싸는 외곽선과 이름을 표시
 */
const GroupBoundingBox = memo(({ 
  group, 
  dashboardTemplate, 
  layout,
  containerWidth,
  isSelected = false,
  selectedComponentIds,
  onClick,
  onGroupResize,
  onSelectGroupComponents,  // 그룹 전체 선택 핸들러
  onGroupDrag,  // 그룹 드래그 핸들러
  onContextMenu  // 컨텍스트 메뉴 핸들러 추가
}) => {
  // 모든 Hooks를 최상단에 선언
  const [isResizing, setIsResizing] = useState(false);
  const [resizeHandle, setResizeHandle] = useState(null);
  const [previewBounds, setPreviewBounds] = useState(null); // 리사이즈 프리뷰
  const resizeStartPos = useRef({ x: 0, y: 0 });
  const originalBounds = useRef(null);
  
  // 드래그 상태
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 }); // 드래그 중 offset
  const dragStartPos = useRef({ x: 0, y: 0 });
  const originalTransforms = useRef({}); // 드래그 시작 시 각 차트의 원래 transform
  
  // 바운딩 박스 계산
  const boundingBox = useMemo(() => {
    if (!group || !dashboardTemplate || !layout) return null;
    return getGroupBoundingBox(dashboardTemplate, layout, group.id);
  }, [group, dashboardTemplate, layout]);

  // 그리드 설정
  const columns = dashboardTemplate?.layout?.columns || 12;
  const rowHeight = calculateRowHeight(dashboardTemplate, containerWidth);
  const gridSpacing = dashboardTemplate?.dashboard_options?.grid_spacing ?? 10;

  // 잠금 상태 확인
  const isLocked = group?.locked === true;

  // 드래그 시작
  const handleDragStart = useCallback((e) => {
    if (isLocked || !boundingBox) return;
    
    // 리사이즈 핸들이나 그룹 라벨에서 시작한 경우 무시
    if (e.target.classList.contains('resize-handle') || 
        e.target.closest('.group-label')) {
      return;
    }
    
    e.stopPropagation();
    e.preventDefault();
    
    // ⭐ 드래그 시작 시 그룹 전체를 선택
    if (onSelectGroupComponents && group?.id) {
      onSelectGroupComponents(group.id);
    }
    
    // ⭐ 각 차트의 현재 transform 저장
    const componentIds = getGroupComponentIds(dashboardTemplate, group.id);
    const transforms = {};
    componentIds.forEach(componentId => {
      const element = document.querySelector(`[data-component-id="${componentId}"]`);
      if (element) {
        const style = window.getComputedStyle(element);
        const currentTransform = style.transform;
        let currentX = 0, currentY = 0;
        
        if (currentTransform && currentTransform !== 'none') {
          const matrix = currentTransform.match(/matrix\(([^)]+)\)/);
          if (matrix) {
            const values = matrix[1].split(', ');
            currentX = parseFloat(values[4]) || 0;
            currentY = parseFloat(values[5]) || 0;
          }
        }
        
        transforms[componentId] = { x: currentX, y: currentY };
      }
    });
    originalTransforms.current = transforms;
    
    setIsDragging(true);
    setDragOffset({ x: 0, y: 0 }); // offset 초기화
    dragStartPos.current = { x: e.clientX, y: e.clientY };
  }, [isLocked, boundingBox, onSelectGroupComponents, group?.id, dashboardTemplate]);
  
  // 리사이즈 핸들 mousedown
  const handleResizeStart = useCallback((e, handle) => {
    if (isLocked || !boundingBox) return;
    e.stopPropagation();
    e.preventDefault();
    
    // ⭐ 리사이즈 시작 시 그룹 전체를 선택 (일부만 선택된 경우 전체 선택)
    if (onSelectGroupComponents && group?.id) {
      onSelectGroupComponents(group.id);
    }
    
    setIsResizing(true);
    setResizeHandle(handle);
    setPreviewBounds(null);
    resizeStartPos.current = { x: e.clientX, y: e.clientY };
    originalBounds.current = { ...boundingBox };
  }, [isLocked, boundingBox, onSelectGroupComponents, group?.id]);

  // 리사이즈 진행
  const handleResizeMove = useCallback((e) => {
    if (!isResizing || !resizeHandle || !originalBounds.current) return;
    
    const deltaX = e.clientX - resizeStartPos.current.x;
    const deltaY = e.clientY - resizeStartPos.current.y;
    
    // 그리드 단위로 변환
    const cellWidth = (containerWidth - (gridSpacing * (columns - 1))) / columns;
    const deltaGridX = Math.round(deltaX / (cellWidth + gridSpacing));
    const deltaGridY = Math.round(deltaY / (rowHeight + gridSpacing));
    
    let newBounds = { ...originalBounds.current };
    
    // 핸들 위치에 따라 크기 조정
    switch (resizeHandle) {
      case 'se': // 남동쪽
        newBounds.w = Math.max(1, originalBounds.current.w + deltaGridX);
        newBounds.h = Math.max(1, originalBounds.current.h + deltaGridY);
        break;
      case 'sw': // 남서쪽
        newBounds.x = originalBounds.current.x + deltaGridX;
        newBounds.w = Math.max(1, originalBounds.current.w - deltaGridX);
        newBounds.h = Math.max(1, originalBounds.current.h + deltaGridY);
        break;
      case 'ne': // 북동쪽
        newBounds.y = originalBounds.current.y + deltaGridY;
        newBounds.w = Math.max(1, originalBounds.current.w + deltaGridX);
        newBounds.h = Math.max(1, originalBounds.current.h - deltaGridY);
        break;
      case 'nw': // 북서쪽
        newBounds.x = originalBounds.current.x + deltaGridX;
        newBounds.y = originalBounds.current.y + deltaGridY;
        newBounds.w = Math.max(1, originalBounds.current.w - deltaGridX);
        newBounds.h = Math.max(1, originalBounds.current.h - deltaGridY);
        break;
      case 'e': // 동쪽
        newBounds.w = Math.max(1, originalBounds.current.w + deltaGridX);
        break;
      case 'w': // 서쪽
        newBounds.x = originalBounds.current.x + deltaGridX;
        newBounds.w = Math.max(1, originalBounds.current.w - deltaGridX);
        break;
      case 's': // 남쪽
        newBounds.h = Math.max(1, originalBounds.current.h + deltaGridY);
        break;
      case 'n': // 북쪽
        newBounds.y = originalBounds.current.y + deltaGridY;
        newBounds.h = Math.max(1, originalBounds.current.h - deltaGridY);
        break;
      default:
        break;
    }
    
    // 프리뷰 상태만 업데이트 (실제 레이아웃은 마우스를 놓을 때 업데이트)
    setPreviewBounds(newBounds);
  }, [isResizing, resizeHandle, containerWidth, columns, rowHeight, gridSpacing]);

  // 드래그 진행
  const handleDragMove = useCallback((e) => {
    if (!isDragging || !boundingBox) return;
    
    const deltaX = e.clientX - dragStartPos.current.x;
    const deltaY = e.clientY - dragStartPos.current.y;
    
    // 실시간으로 offset 업데이트 (transform 적용용)
    setDragOffset({ x: deltaX, y: deltaY });
  }, [isDragging, boundingBox]);
  
  // 드래그 종료
  const handleDragEnd = useCallback((e) => {
    if (!isDragging || !boundingBox) return;
    
    const deltaX = e.clientX - dragStartPos.current.x;
    const deltaY = e.clientY - dragStartPos.current.y;
    
    // 그리드 단위로 변환
    const cellWidth = (containerWidth - (gridSpacing * (columns - 1))) / columns;
    const deltaGridX = Math.round(deltaX / (cellWidth + gridSpacing));
    const deltaGridY = Math.round(deltaY / (rowHeight + gridSpacing));
    
    // 드래그 상태 초기화
    setIsDragging(false);
    setDragOffset({ x: 0, y: 0 });
    
    // 차트들의 transform을 원래 상태로 복원
    const componentIds = getGroupComponentIds(dashboardTemplate, group.id);
    componentIds.forEach(componentId => {
      const element = document.querySelector(`[data-component-id="${componentId}"]`);
      if (element) {
        const originalTransform = originalTransforms.current[componentId];
        
        // 원래 transform 상태로 복원
        if (originalTransform && (originalTransform.x !== 0 || originalTransform.y !== 0)) {
          element.style.transform = `translate(${originalTransform.x}px, ${originalTransform.y}px)`;
        } else {
          element.style.transform = '';
        }
        
        element.style.transition = '';
        element.style.zIndex = '';
      }
    });
    
    // originalTransforms 초기화
    originalTransforms.current = {};
    
    // 의미 있는 이동이 있었으면 그룹 드래그 핸들러 호출
    if ((deltaGridX !== 0 || deltaGridY !== 0) && onGroupDrag) {
      onGroupDrag(group.id, { deltaX: deltaGridX, deltaY: deltaGridY });
    }
  }, [isDragging, boundingBox, containerWidth, columns, rowHeight, gridSpacing, onGroupDrag, group?.id, dashboardTemplate]);
  
  // 리사이즈 종료
  const handleResizeEnd = useCallback(() => {
    // 프리뷰 bounds가 있으면 실제 레이아웃 업데이트
    if (previewBounds && onGroupResize) {
      onGroupResize(group.id, previewBounds);
    }
    
    setIsResizing(false);
    setResizeHandle(null);
    setPreviewBounds(null);
  }, [previewBounds, onGroupResize, group.id]);

  // 전역 이벤트 리스너 (리사이즈)
  React.useEffect(() => {
    if (isResizing) {
      document.addEventListener('mousemove', handleResizeMove);
      document.addEventListener('mouseup', handleResizeEnd);
      return () => {
        document.removeEventListener('mousemove', handleResizeMove);
        document.removeEventListener('mouseup', handleResizeEnd);
      };
    }
  }, [isResizing, handleResizeMove, handleResizeEnd]);
  
  // 전역 이벤트 리스너 (드래그)
  React.useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleDragMove);
      document.addEventListener('mouseup', handleDragEnd);
      return () => {
        document.removeEventListener('mousemove', handleDragMove);
        document.removeEventListener('mouseup', handleDragEnd);
      };
    }
  }, [isDragging, handleDragMove, handleDragEnd]);
  
  // 드래그 중 실시간 transform 적용
  React.useEffect(() => {
    if (isDragging && dragOffset && (dragOffset.x !== 0 || dragOffset.y !== 0)) {
      // 그룹 내 모든 차트에 transform 적용 (원래 transform + offset)
      const componentIds = getGroupComponentIds(dashboardTemplate, group.id);
      
      componentIds.forEach(componentId => {
        const element = document.querySelector(`[data-component-id="${componentId}"]`);
        if (element) {
          // 원래 transform에 offset을 더함
          const originalTransform = originalTransforms.current[componentId] || { x: 0, y: 0 };
          const newX = originalTransform.x + dragOffset.x;
          const newY = originalTransform.y + dragOffset.y;
          
          element.style.transform = `translate(${newX}px, ${newY}px)`;
          element.style.transition = 'none';
          element.style.zIndex = '1000';
        }
      });
    }
  }, [isDragging, dragOffset, dashboardTemplate, group?.id]);

  // 리사이즈 핸들 렌더링
  const renderResizeHandles = useCallback(() => {
    if (!isSelected || isLocked) return null;
    
    const handles = [
      { position: 'nw', cursor: 'nw-resize' },
      { position: 'n', cursor: 'n-resize' },
      { position: 'ne', cursor: 'ne-resize' },
      { position: 'e', cursor: 'e-resize' },
      { position: 'se', cursor: 'se-resize' },
      { position: 's', cursor: 's-resize' },
      { position: 'sw', cursor: 'sw-resize' },
      { position: 'w', cursor: 'w-resize' }
    ];
    
    return handles.map(({ position, cursor }) => (
      <div
        key={position}
        className={`resize-handle resize-handle-${position}`}
        style={{ cursor }}
        onMouseDown={(e) => handleResizeStart(e, position)}
      />
    ));
  }, [isSelected, isLocked, handleResizeStart]);

  // Early return은 모든 Hooks 이후에
  if (!boundingBox) return null;

  // 리사이즈 중에는 previewBounds 사용, 아니면 실제 boundingBox 사용
  const effectiveBounds = isResizing && previewBounds ? previewBounds : boundingBox;

  // 픽셀 단위 위치/크기 계산
  // React Grid Layout의 실제 계산 방식과 동일하게
  const cellWidth = (containerWidth - (gridSpacing * (columns - 1))) / columns;
  const left = effectiveBounds.x * (cellWidth + gridSpacing);
  const top = effectiveBounds.y * (rowHeight + gridSpacing);
  const width = effectiveBounds.w * cellWidth + (effectiveBounds.w - 1) * gridSpacing;
  const height = effectiveBounds.h * rowHeight + (effectiveBounds.h - 1) * gridSpacing;
  
  // 바운딩 박스에 여유 공간 추가 (더 잘 감싸도록)
  const padding = 8; // 8px 패딩
  const adjustedLeft = left - padding;
  const adjustedTop = top - padding;
  const adjustedWidth = width + (padding * 2);
  const adjustedHeight = height + (padding * 2);

  return (
    <div
      className={`group-bounding-box ${isSelected ? 'selected' : ''} ${isLocked ? 'locked' : ''} ${isResizing ? 'resizing' : ''} ${isDragging ? 'dragging' : ''}`}
      data-group-id={group.id}
      style={{
        position: 'absolute',
        left: `${adjustedLeft}px`,
        top: `${adjustedTop}px`,
        width: `${adjustedWidth}px`,
        height: `${adjustedHeight}px`,
        pointerEvents: 'none', // 내부 차트의 이벤트를 방해하지 않음
        zIndex: 1, // 컴포넌트(z-index: 2)보다 아래
        transition: (isResizing || isDragging) ? 'none' : 'all 0.2s ease', // 리사이즈/드래그 중에는 transition 비활성화
        transform: isDragging ? `translate(${dragOffset.x}px, ${dragOffset.y}px)` : 'none' // 드래그 중 실시간 이동
      }}
    >
      {/* 클릭/드래그 가능한 테두리 영역 (4개의 투명 div) */}
      {/* 위쪽 테두리 */}
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '15px',
          pointerEvents: 'auto',
          cursor: isDragging ? 'grabbing' : 'grab'
        }}
        onMouseDown={handleDragStart}
        onClick={(e) => {
          e.stopPropagation();
          if (onClick) onClick();
        }}
        onContextMenu={(e) => {
          e.preventDefault();
          e.stopPropagation();
          if (onContextMenu) {
            onContextMenu(e, group.id);
          }
        }}
      />
      {/* 아래쪽 테두리 */}
      <div
        style={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          height: '15px',
          pointerEvents: 'auto',
          cursor: isDragging ? 'grabbing' : 'grab'
        }}
        onMouseDown={handleDragStart}
        onClick={(e) => {
          e.stopPropagation();
          if (onClick) onClick();
        }}
        onContextMenu={(e) => {
          e.preventDefault();
          e.stopPropagation();
          if (onContextMenu) {
            onContextMenu(e, group.id);
          }
        }}
      />
      {/* 왼쪽 테두리 */}
      <div
        style={{
          position: 'absolute',
          top: '15px',
          bottom: '15px',
          left: 0,
          width: '15px',
          pointerEvents: 'auto',
          cursor: isDragging ? 'grabbing' : 'grab'
        }}
        onMouseDown={handleDragStart}
        onClick={(e) => {
          e.stopPropagation();
          if (onClick) onClick();
        }}
        onContextMenu={(e) => {
          e.preventDefault();
          e.stopPropagation();
          if (onContextMenu) {
            onContextMenu(e, group.id);
          }
        }}
      />
      {/* 오른쪽 테두리 */}
      <div
        style={{
          position: 'absolute',
          top: '15px',
          bottom: '15px',
          right: 0,
          width: '15px',
          pointerEvents: 'auto',
          cursor: isDragging ? 'grabbing' : 'grab'
        }}
        onMouseDown={handleDragStart}
        onClick={(e) => {
          e.stopPropagation();
          if (onClick) onClick();
        }}
        onContextMenu={(e) => {
          e.preventDefault();
          e.stopPropagation();
          if (onContextMenu) {
            onContextMenu(e, group.id);
          }
        }}
      />
      
      {/* 그룹 라벨 */}
      <div 
        className="group-label"
        onClick={(e) => {
          e.stopPropagation();
          if (onClick) {
            onClick();
          }
        }}
        onContextMenu={(e) => {
          e.preventDefault();
          e.stopPropagation();
          if (onContextMenu) {
            onContextMenu(e, group.id);
          }
        }}
      >
        <span className="group-name">{group.name}</span>
        {isLocked && <span className="lock-icon">🔒</span>}
      </div>
      
      {/* 리사이즈 핸들들 */}
      {renderResizeHandles()}
    </div>
  );
});

GroupBoundingBox.displayName = 'GroupBoundingBox';

export default GroupBoundingBox;

